// src/feed/types.ts
//
// Single source of truth for the feed feature's data model.
//
// Design rationale (so future-you doesn't rip this up in three months):
//
// • Visibility is THREE tiers, matching the convention used by Instagram,
//   LinkedIn, and Twitter: private (just me), followers (people I've
//   approved), public (anyone). The wire format intentionally uses
//   English string literals — we don't translate the storage format,
//   only the UI labels via useT(). This keeps cross-device + cross-tool
//   reads sane.
//
// • A FeedItem snapshots its source content (caption, image_path,
//   location, etc.) at write time. Reads don't need to join to the
//   source doc. Source-doc deletes don't break feed history. Source-doc
//   edits don't cascade — that's deliberate; once you've "posted to
//   feed" you've published a snapshot.
//
// • The ItemKind taxonomy is the union of all current AND planned
//   sources. Adding a new kind later is a non-breaking change as long
//   as the feed renderer falls back gracefully on unknown kinds.
//
// • Reaction + comment models live here as types but the v1 ship doesn't
//   wire them up. Schema-first prevents a rewrite when phase 2 lands.
//
// • Followers use the Instagram model: private accounts approve each
//   follow request; public accounts can be followed by anyone. The
//   `is_account_private` flag lives on the user profile, not on the
//   feed item. Per-item visibility still applies regardless of account
//   privacy — a public account can still post a 'private' (just me)
//   item.

import { Timestamp } from 'firebase/firestore';

/** Reaction kinds. Heart is the v1 reaction (LinkedIn/Instagram-
 *  style "love"). thumbs_up reserved for symmetry with the
 *  save-to-feed bookmark on chat messages — different surface,
 *  different semantic. */
export type ReactionType = 'heart' | 'thumbs_up';

/**
 * Visibility tiers. Wire format is English; UI labels go through
 * useT(). Order is significant: each tier is a strict superset of
 * the one before it (private ⊂ followers ⊂ public).
 */
export type FeedVisibility = 'private' | 'followers' | 'public';

/**
 * The set of item sources we know how to render today, plus the
 * planned phase-2 sources. Renderer falls back to a generic card
 * for unknown kinds, so adding a new value here doesn't break old
 * clients.
 */
export type FeedItemKind =
  | 'postcard'           // Visit-grouped POI bundle (auto-created)
  | 'photo'              // User explicitly posts a photo from chat
  | 'saved_message'      // Thumbs-up on an AI message in chat
  | 'guide_save'         // Heart on a guide card (phase 2)
  | 'tour_complete'      // Walking tour wrap-up (phase 2)
  | 'shared_reaction'    // Phase 2: "X liked Y's post" feed entries
  | 'session_recap';     // End-of-session summary card (Phase 5B)

/** Snapshot fields. Whichever subset is relevant per kind. */
export interface FeedItemSnapshot {
  /** Optional human-readable caption / lead text. */
  caption?: string;
  /** Optional storage path to a hero image. */
  image_path?: string;
  /** Optional preview URL (signed/blob/data) for fast first paint. */
  preview_url?: string;
  /** Optional location for items anchored to a place. lat/lng are
   *  optional because some sources (guide cards about a city in
   *  general — tips, stories, prompts) only have a textual place
   *  name like "Berlin, Germany" without a specific point. The
   *  renderer should prefer place_name when present. */
  location?: {
    lat?: number;
    lng?: number;
    address?: string;
    place_name?: string;
  };
  /** Optional rich body (used by saved_message — the AI's reply). */
  body?: string;
  /** Optional title. Used by postcard (POI name), tour_complete
   *  (tour name), etc. */
  title?: string;
}

/** Reference to the source doc that originated this feed item.
 *  Renderer can deep-link back when needed (e.g. "open conversation"). */
export interface FeedItemSource {
  kind:
    | 'visit'              // landmarks/visits collection
    | 'message'            // conversation message
    | 'guide_card'         // guide card (phase 2)
    | 'tour'               // walking tour (phase 2)
    | 'manual';            // user-composed, no source
  id: string;
  /** For source.kind === 'message': also need the conversation id. */
  conversation_id?: string;
}

/**
 * The feed_item document. Top-level collection /feed_items/{item_id}.
 * Indexed on owner_id, created_at desc, visibility for the home-feed
 * query. The home-feed query is "items I own + items from people I
 * follow, where visibility allows me to see them."
 */
export interface FeedItem {
  id: string;
  owner_id: string;
  /** Snapshot of the author's identity at write time. Snapshotted
   *  (not joined) for the same reason as `snapshot`: feeds render
   *  without per-item profile fetches, and renames don't retro-edit
   *  feed history. Phase 2 may add a "this user has changed their
   *  name since this post" indicator — easy to add later by
   *  comparing snapshot vs current.
   *
   *  `display_name` falls back to "Anonymous" when the user has no
   *  set displayName. `photo_url` is omitted when not available;
   *  the renderer falls back to a colored initial. */
  author: {
    display_name: string;
    photo_url?: string;
  };
  kind: FeedItemKind;
  visibility: FeedVisibility;
  created_at: Timestamp | null;
  updated_at: Timestamp | null;
  snapshot: FeedItemSnapshot;
  source: FeedItemSource;

  // Phase-2 denormalized counters. Always present in the schema (default
  // 0) so the renderer can show counts uniformly without a fallback.
  reactions_count: number;
  comments_count: number;

  /** Phase-2 only: when this is a 'shared_reaction', what was reacted
   *  to. Lets followers' feeds display "Daniel liked Jane's postcard." */
  reaction_target?: {
    item_id: string;
    owner_id: string;
    reaction_type: 'thumbs_up' | 'heart';
  };

  /** When true, the item is readable by ANYONE — authenticated or
   *  not — bypassing the visibility model. Owner toggles this on by
   *  tapping "Share" and gets a public /post/{id} URL; toggling it
   *  off revokes external access immediately (existing tabs lose
   *  access on next read). The visibility field is preserved
   *  underneath so when share_enabled flips back to false the item
   *  returns to its original visibility (private/followers/public).
   *  Default false; field is optional in the doc to avoid mass
   *  backfill. */
  share_enabled?: boolean;

  /** Visibility ceiling inherited from the source item this is
   *  derived from. Set ONLY on derived items — `shared_reaction`
   *  today, potentially repost-style kinds in the future. The cap
   *  is the convention every consent-based social system follows:
   *  a reactor can never make a derivative more visible than the
   *  original author chose. Owner of the derived item can DOWNgrade
   *  freely (private is always allowed); Firestore rules block
   *  attempts to set visibility above this ceiling, and block
   *  share_enabled when this is anything but 'public'.
   *
   *  This field is ADMIN-WRITE-ONLY. The reactions-cascade Cloud
   *  Function sets it at creation time and the source-visibility-
   *  change cascade keeps it in sync; clients cannot set or modify
   *  it (the rules enforce that source_visibility is unchanged on
   *  any client update). */
  source_visibility?: FeedVisibility;

  /** Session-recap extras. Present only on kind='session_recap'.
   *  Lives alongside snapshot (not inside it) so generic snapshot
   *  consumers don't have to know about recap-specific fields, but
   *  the recap card renderer can use them for the stat strip and
   *  tap-through to the originating visits. */
  recap?: {
    visit_count: number;
    visit_ids: string[];
    distance_m: number;
    duration_min: number;
    finds: Array<{ collection_title: string; item_title: string }>;
    session_window_bucket?: number;
    session_window_ended_at?: Timestamp | null;
  };

  /** When this postcard groups multiple visits (sibling/revisit
   *  children of the source visit), these are the supplementary
   *  photos beyond the primary one in snapshot.image_path. The
   *  renderer shows a "+N photos" badge or a swipeable carousel.
   *  Present only on kind='postcard' when grouping occurred. */
  additional_photos?: Array<{
    visit_id: string;
    photo_reference?: string;
    visit_kind?: string;
    verified_at?: Timestamp | null;
  }>;
  /** Total photo count for this postcard = 1 (primary) +
   *  additional_photos.length. Denormalized so the renderer can show
   *  the count without computing the array length. */
  photo_count?: number;
}

/** Per-user defaults for "what visibility should a new <kind> get?" */
export interface FeedDefaults {
  postcard: FeedVisibility;
  photo: FeedVisibility;
  saved_message: FeedVisibility;
  guide_save: FeedVisibility;
  tour_complete: FeedVisibility;
}

/** Hardcoded defaults used when the user has no preferences doc yet.
 *  Reasoned per-kind:
 *    - postcard: 'followers' — visit imagery is naturally shareable
 *      with one's network but not the broader internet by default.
 *    - photo: 'public' — explicit "post to feed" implies opt-in
 *      sharing; if they wanted private they'd just attach the photo
 *      without toggling post-to-feed.
 *    - saved_message: 'private' — thumbs-up means "I want to come
 *      back to this" → personal record, not broadcast.
 *    - guide_save / tour_complete: 'followers' — meaningful
 *      milestones, default to network-visible.
 *
 *  Users can customize all of these in settings.
 */
export const HARDCODED_FEED_DEFAULTS: FeedDefaults = {
  postcard: 'followers',
  photo: 'public',
  saved_message: 'private',
  guide_save: 'followers',
  tour_complete: 'followers',
};

// ── Followers schema (designed for, not exposed in v1 UI) ──────
//
// /users/{uid}/followers/{follower_uid}      → { followed_at }
// /users/{uid}/following/{followed_uid}      → { followed_at }
// /users/{uid}/follow_requests/{requester_uid} → { requested_at }
//
// To follow a public account: write directly to followers/following.
// To follow a private account: write to follow_requests, target user
// approves by moving doc into followers/.
//
// `is_account_private` flag lives on /users/{uid} profile doc.

export interface FollowerEntry {
  followed_at: Timestamp;
}

export interface FollowRequestEntry {
  requested_at: Timestamp;
}
